import React, { useEffect, useRef, useState } from "react";
import { Button, Form, message, Spin } from "antd";
import { ArrowLeftOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import logo from "../../../Assets/Images/logo.svg";
import logoCompany from "../../../Assets/Icons/companylogo.svg";
import InputCard from "../../../Components/Cards/InputCard";
import FuelType from "../../../Components/Cards/FuelTypeCard";
import GenericOperatingHours from "../../../Components/Tables/GenericOperatingHourTables";
import GenericDocumentManagerTable from "../../../Components/Tables/GenericDocumentManagerTable";
import LocationMap from "../../../Components/Map/LocationPicker";
import {
  getFuelVendorRevisionRequest,
  submitFuelVendorRevision,
  deleteFuelVendorRevisionDocument,
} from "../../../API/Public/FuelVendorRevise";
import {
  getFuelTypes,
  getVendorTypes,
  getFuelBrands,
  getDocumentCategories,
} from "../../../API/Config";
import { getToken } from "../../../Utlis/UpdateUserState";
import "./style.css";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveFileUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${BACKEND_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;
};

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const FuelVendorRevise = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const watchedLatitude = Form.useWatch("latitude", form);
  const watchedLongitude = Form.useWatch("longitude", form);
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");

  const [avatar, setAvatar] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [selectedFuels, setSelectedFuels] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [operatingHours, setOperatingHours] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [availableFuelTypes, setAvailableFuelTypes] = useState([]);
  const [vendorTypeOptions, setVendorTypeOptions] = useState([]);
  const [fuelBrandOptions, setFuelBrandOptions] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchFuelTypes = async () => {
      const res = await getFuelTypes();
      const types = res?.data?.data || res?.data?.results || res?.data;
      if (Array.isArray(types)) setAvailableFuelTypes(types);
    };

    const fetchVendorTypes = async () => {
      try {
        const response = await getVendorTypes();
        const list = Array.isArray(response?.data)
          ? response.data
          : response?.data?.data || response?.data?.results || [];

        if (Math.floor(response?.status / 100) === 2 && Array.isArray(list)) {
          setVendorTypeOptions(
            list.map((type) =>
              typeof type === "string"
                ? type
                : { label: type.name, value: type.name },
            ),
          );
        }
      } catch (err) {
        console.error("Failed to load vendor types:", err);
      }
    };

    const fetchFuelBrands = async () => {
      try {
        const response = await getFuelBrands();
        const list = Array.isArray(response?.data)
          ? response.data
          : response?.data?.data || response?.data?.results || [];

        if (Math.floor(response?.status / 100) === 2 && Array.isArray(list)) {
          setFuelBrandOptions(
            list.map((brand) =>
              typeof brand === "string"
                ? { label: brand, value: brand }
                : { label: brand.name, value: brand.id ?? brand.name },
            ),
          );
        }
      } catch (err) {
        console.error("Failed to load fuel brands:", err);
      }
    };

    const fetchDocumentCategories = async () => {
      const res = await getDocumentCategories("fuel-vendor");
      const list = res?.data?.data;
      if (res?.status === 200 && Array.isArray(list)) {
        setDocumentCategories(
          list.map((cat) => ({
            label: cat.document_name,
            value: cat.document_name,
            allowedFormats: cat.allowed_formats,
          })),
        );
      }
    };

    fetchFuelTypes();
    fetchVendorTypes();
    fetchFuelBrands();
    fetchDocumentCategories();
  }, []);

  useEffect(() => {
    if (!token && !getToken()) {
      setLoadError("This revision link is invalid or missing a token.");
      setLoading(false);
      return;
    }

    const fetchRequest = async () => {
      try {
        const res = await getFuelVendorRevisionRequest(token);
        const data = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && data) {
          const entity = data.entity || {};

          form.setFieldsValue({
            legalEntityName: entity.legal_name || "",
            brandName: entity.brand_name || "",
            taxId: entity.tax_id || "",
            vendorType: entity.vendor_type || "",
            companyContactNumber: entity.contact_phone || "",
            hqAddress: entity.address || "",
            primaryContact: entity.primary_contact_person || "",
            businessEmail: entity.contact_email || "",
            phoneNumber: entity.registration_contact_phone || "",
            bankName: entity.bank_details?.bank_name || "",
            bankAddress: entity.bank_details?.bank_address || "",
            accountHolderName: entity.bank_details?.account_holder_name || "",
            accountNumber: entity.bank_details?.account_number || "",
            accountType: entity.bank_details?.account_type || "",
            swiftCode: entity.bank_details?.swift_code || "",
            currency: entity.currency || "JMD",
            stationName: entity.station_name || "",
            stationId: entity.station_id || "",
            stationAddress: entity.station_address || "",
            fuelBrand: entity.fuel_brand?.id ?? entity.fuel_brand ?? undefined,
            latitude: entity.latitude ?? "",
            longitude: entity.longitude ?? "",
          });

          setAvatar(resolveFileUrl(entity.avatar));
          setSelectedFuels(
            Array.isArray(entity.fuel_types_offered)
              ? entity.fuel_types_offered.map((fuel) => fuel.name)
              : [],
          );
          setOperatingHours(
            Array.isArray(entity.operating_hours) ? entity.operating_hours : [],
          );
          setDocuments(
            Array.isArray(entity.documents)
              ? entity.documents.map((doc) => ({
                  id: doc.id,
                  key: String(doc.id),
                  name:
                    doc.document_name ||
                    (doc.file ? doc.file.split("/").pop() : doc.category) ||
                    "-",
                  category: doc.category || "-",
                  allowedFormats: doc.allowed_formats || [],
                  date: doc.uploaded_at
                    ? new Date(doc.uploaded_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "-",
                  expiryDate: doc.expiry_date || null,
                  fileUrl: resolveFileUrl(doc.file),
                }))
              : [],
          );
          setReviewNotes(data.audit_notes || "");
        } else {
          setLoadError(
            getErrorMessage(res?.data, "Unable to load this revision request."),
          );
        }
      } catch (err) {
        setLoadError(
          getErrorMessage(
            err?.response?.data,
            "Unable to load this revision request.",
          ),
        );
      } finally {
        setLoading(false);
      }
    };

    fetchRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = () => setAvatar(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!selectedFuels.length) {
        message.error("Please select at least one fuel type.");
        return;
      }

      const formData = new FormData();
      formData.append("legal_name", values.legalEntityName);
      formData.append("brand_name", values.brandName);
      formData.append("tax_id", values.taxId);
      formData.append("vendor_type", values.vendorType);
      formData.append("contact_phone", values.companyContactNumber);
      formData.append("address", values.hqAddress);
      formData.append("primary_contact_person", values.primaryContact);
      formData.append("contact_email", values.businessEmail);
      formData.append("registration_contact_phone", values.phoneNumber);
      formData.append(
        "bank_details",
        JSON.stringify({
          bank_name: values.bankName || "",
          bank_address: values.bankAddress || "",
          account_holder_name: values.accountHolderName || "",
          account_number: values.accountNumber || "",
          account_type: values.accountType || "",
          swift_code: values.swiftCode || "",
        }),
      );
      formData.append("currency", values.currency);
      formData.append("station_name", values.stationName);
      formData.append("station_id", values.stationId);
      formData.append("station_address", values.stationAddress);
      formData.append("fuel_brand_id", values.fuelBrand || "");
      formData.append("latitude", values.latitude);
      formData.append("longitude", values.longitude);
      if (avatarFile) {
        formData.append("avatar", avatarFile);
      }

      const fuelTypeIdByName = {};
      availableFuelTypes.forEach((f) => {
        fuelTypeIdByName[f.name] = f.id;
      });
      selectedFuels
        .map((name) => fuelTypeIdByName[name])
        .filter((id) => id !== undefined)
        .forEach((id) => {
          formData.append("fuel_types_offered", id);
        });

      formData.append("operating_hours", JSON.stringify(operatingHours));

      const newDocuments = documents.filter((doc) => doc.file);
      newDocuments.forEach((doc) => {
        formData.append("documents", doc.file);
      });
      if (newDocuments.length > 0) {
        formData.append(
          "document_categories",
          JSON.stringify(newDocuments.map((doc) => doc.category)),
        );
        formData.append(
          "document_expiry_dates",
          JSON.stringify(newDocuments.map((doc) => doc.expiryDate || null)),
        );
      }

      setSubmitting(true);
      const res = await submitFuelVendorRevision(token, formData);

      if (Math.floor(res?.status / 100) === 2) {
        message.success(
          res?.data?.message || "Your revised application has been submitted.",
        );
        setTimeout(() => {
          localStorage.clear();
          window.location.href = "/login-vendor";
        }, 1500);
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to submit revised application."),
        );
      }
    } catch (err) {
      if (err?.errorFields) return;
      message.error(
        getErrorMessage(
          err?.response?.data,
          "Failed to submit revised application.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const registrationFields = [
    { name: "legalEntityName", label: "Legal Entity Name", type: "text", fullWidth: true, placeholder: "Enter Legal Entity Name", rules: [{ required: true, message: "Legal entity name is required" }] },
    { name: "brandName", label: "Brand Name", type: "text", placeholder: "Enter Brand Name", rules: [{ required: true, message: "Brand name is required" }] },
    { name: "taxId", label: "Tax ID / EIN Number", type: "text", placeholder: "e.g. 74-1234567", rules: [{ required: true, message: "Tax ID is required" }] },
    { name: "vendorType", label: "Vendor Type", type: "select", options: vendorTypeOptions, placeholder: "Select Vendor Type", rules: [{ required: true, message: "Vendor type is required" }] },
    { name: "companyContactNumber", label: "Company Contact Number", type: "phone", placeholder: "+1 (000) 000-0000", rules: [{ required: true, message: "Contact number is required" }] },
  ];

  const contactFields = [
    { name: "hqAddress", label: "Headquarters Address", type: "textarea", fullWidth: true, placeholder: "Enter full headquarters address", rules: [{ required: true, message: "Headquarters address is required" }] },
    { name: "primaryContact", label: "Primary Contact Person", type: "text", placeholder: "Full Name", rules: [{ required: true, message: "Primary contact is required" }] },
    { name: "businessEmail", label: "Business Email", type: "text", placeholder: "email@company.com", rules: [{ required: true, message: "Email is required" }, { type: "email", message: "Enter a valid email" }] },
    { name: "phoneNumber", label: "Phone Number", type: "phone", placeholder: "+1 (000) 000-0000", rules: [{ required: true, message: "Phone number is required" }] },
  ];

  const bankingFields = [
    { name: "bankName", label: "Bank Name", type: "text", placeholder: "Enter Bank Name", rules: [{ required: true, message: "Bank name is required" }] },
    { name: "bankAddress", label: "Bank Address", type: "text", placeholder: "Enter Bank Address", rules: [{ required: true, message: "Bank address is required" }] },
    { name: "accountHolderName", label: "Account Holder Name", type: "text", placeholder: "Enter Account Holder Name", rules: [{ required: true, message: "Account holder name is required" }] },
    { name: "accountNumber", label: "Account Number", type: "text", placeholder: "Enter Account Number", rules: [{ required: true, message: "Account number is required" }] },
    { name: "accountType", label: "Account Type", type: "select", options: ["Savings", "Current", "Checking"], placeholder: "Select", rules: [{ required: true, message: "Account type is required" }] },
    { name: "swiftCode", label: "SWIFT / BIC Code", type: "text", placeholder: "e.g. SWIFT-ABC-0123", rules: [{ required: true, message: "SWIFT/BIC code is required" }] },
    { name: "currency", label: "Currency", type: "currency", placeholder: "Select currency", rules: [{ required: true, message: "Currency is required" }] },
  ];

  const stationFields = [
    { name: "stationName", label: "Station Name", type: "text", placeholder: "Enter Station Name", rules: [{ required: true, message: "Station name is required" }] },
    { name: "stationId", label: "Station ID", type: "text", placeholder: "e.g. STN-2024-8810-X91", rules: [{ required: true, message: "Station ID is required" }] },
    { name: "stationAddress", label: "Full Physical Address", type: "textarea", fullWidth: true, placeholder: "e.g. 1 Business Parkway, Suite 100, Houston TX", rules: [{ required: true, message: "Station address is required" }] },
    { name: "fuelBrand", label: "Fuel Brand", type: "select", placeholder: "Select Fuel Brand", options: fuelBrandOptions },
    { name: "latitude", label: "Latitude", type: "text", placeholder: "e.g. 40.712", rules: [{ required: true, message: "Latitude is required" }] },
    { name: "longitude", label: "Longitude", type: "text", placeholder: "e.g. -74.006", rules: [{ required: true, message: "Longitude is required" }] },
  ];

  if (loading) {
    return (
      <div
        className="fv-revise fv-revise--status"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          width: "100%",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="fv-revise fv-revise--status">
        <img src={logo} alt="logo" className="fv-revise__logo" />
        <div className="fv-revise__status-text fv-revise__status-text--error">
          {loadError}
        </div>
      </div>
    );
  }

  return (
    <div className="fv-signup fv-revise">
      <div className="fv-signup__logo">
        <img src={logo} alt="logo" />
      </div>
      <div className="fv-signup__page-title">Revise Your Onboarding Request</div>

      {reviewNotes && (
        <div className="fv-revise__notice">
          <InfoCircleOutlined className="fv-revise__notice-icon" />
          <div>
            <div className="fv-revise__notice-title">Reviewer Feedback</div>
            <div className="fv-revise__notice-text">{reviewNotes}</div>
          </div>
        </div>
      )}

      <div className="fv-signup__card">
        <Form form={form} layout="vertical">
          <div className="profile-card">
            <div className="profile-header">
              <div className="btn">
                <Button type="default" className="upload-btn" onClick={handleButtonClick}>
                  Upload Photo
                </Button>
              </div>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <div className="profile-avatar">
                {avatar ? (
                  <img
                    src={avatar}
                    alt="avatar"
                    style={{ width: "100%", height: "100%", borderRadius: "50%" }}
                  />
                ) : (
                  "T"
                )}
              </div>
            </div>
            <div className="profile-body"></div>
          </div>

          <InputCard title="Fuel Vendor Registration" fields={registrationFields} />
          <InputCard title="Contact Information" fields={contactFields} />
          <InputCard title="Banking & Settlement" fields={bankingFields} />
          <InputCard title="Fuel Station Information" fields={stationFields.slice(0, 4)} />
          <InputCard title="" fields={stationFields.slice(4)}>
            <LocationMap
              latitude={watchedLatitude ? parseFloat(watchedLatitude) : null}
              longitude={watchedLongitude ? parseFloat(watchedLongitude) : null}
              label="Station Location"
              onLocationSelect={({ latitude, longitude }) =>
                form.setFieldsValue({ latitude, longitude })
              }
            />
          </InputCard>

          <FuelType
            fuelList={availableFuelTypes.map((f) => f.name)}
            selectedFuel={selectedFuels}
            onSelectFuel={setSelectedFuels}
          />
          <GenericOperatingHours value={operatingHours} onChange={setOperatingHours} />
          <GenericDocumentManagerTable
            setDocuments={setDocuments}
            initialDocuments={documents}
            documentTypes={documentCategories}
            onDeleteDocument={async (doc) => {
              if (!doc?.id) return;

              const res = await deleteFuelVendorRevisionDocument(
                token,
                doc.id,
              );

              if (Math.floor(res?.status / 100) !== 2) {
                throw new Error(
                  getErrorMessage(res?.data, "Failed to delete document."),
                );
              }
            }}
          />
        </Form>

        <div className="btns-row">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Back
          </Button>
          <Button
            type="primary"
            className="submit-btn"
            onClick={handleSubmit}
            loading={submitting}
          >
            Resubmit Application
          </Button>
        </div>

        <div className="fv-signup__powered">
          <img src={logoCompany} alt="CouSync" className="fv-signup__powered-icon" />
          <div className="fv-signup__powered-text">
            Powered by <span>CouSync</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FuelVendorRevise;
